import { ReactNode } from "react";
import { CompleteJson, Table, TableWithShare } from "../../types/DealTracker";
import {
  DealsTableCardContext,
  useDealsTableCard,
} from "../../context/DealsTableCardContext";
import EditableText from "./EditableText";
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ChevronRightIcon,
  EyeIcon,
  PlusCircleIcon,
} from "../Icons";
import HoverExpandButton from "../Shared/HoverExpandButton";
import TableAIAutomateBtn from "./TableAIAutomateBtn";
import PinBtn from "./PinBtn";
import AccessControlBtn from "./AccessControl/AccessControlBtn";
import DeleteTableModal from "./DeleteTableModal";
import FileExportBtn from "./FileExportBtn";
import SaveStatus from "./SaveStatus";
import { useTableTitleUpdater } from "../../hooks/useTableTitleUpdater";

type Props = {
  table: Table;
  rowData: CompleteJson[];
  isTableOpen: boolean;
  isExpanded: boolean;
  hiddenColsCount: number;
  toggleTableOpen: () => void;
  toggleExpand: () => void;
  handleAddRow: () => void;
  handleShowAllHidenCol: () => void;
  onBtnCsvExport: () => void;
  children: ReactNode;
};

export default function DealsTableCard(props: Props) {
  const { children, ...restProps } = props;

  return (
    <DealsTableCardContext.Provider value={restProps}>
      <div
        className={`bg-white rounded-xl p-6 shadow border border-[#f3f3f5] ${
          restProps.isTableOpen ? "space-y-4" : ""
        }`}
      >
        {children}
      </div>
    </DealsTableCardContext.Provider>
  );
}

// header section
DealsTableCard.Header = function Header({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-between">{children}</div>;
};

// action and action buttons
DealsTableCard.HeaderLeftSide = function HeaderLeftSide({
  children,
}: {
  children: ReactNode;
}) {
  return <div className="flex items-center gap-2">{children}</div>;
};

DealsTableCard.Title = function Title() {
  const { table } = useDealsTableCard();
  const updateTitle = useTableTitleUpdater(table);

  const disabled = "shared" in table && table.shared === "viewer";

  return (
    <EditableText table={table} onSubmit={updateTitle} disabled={disabled} />
  );
};

DealsTableCard.Toggle = function Toggle() {
  const { isTableOpen, toggleTableOpen } = useDealsTableCard();

  return (
    <button
      className="p-1 bg-gray-100 text-gray-800 hover:scale-95 hover:text-custom-blue-medium rounded-lg"
      onClick={toggleTableOpen}
      title="Show and hide the table"
    >
      <ChevronRightIcon
        className={`h-5 w-5 transform transition-transform duration-300 ${
          isTableOpen ? "rotate-90" : "rotate-0"
        }`}
        strokeWidth={2.5}
      />
    </button>
  );
};

DealsTableCard.SaveStatus = SaveStatus;

DealsTableCard.PinnedBadge = function PinnedBadge() {
  const { table } = useDealsTableCard();
  if (table.is_pin !== 1) return null;

  return (
    <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold text-green-800 bg-green-100 rounded-full">
      📍Pinned
    </span>
  );
};

DealsTableCard.AccessBadge = function AccessBadge({
  access_type,
}: {
  access_type: TableWithShare["shared"];
}) {
  if (!access_type) return null;

  const config = {
    viewer: {
      label: "View only",
      className: "text-blue-800 bg-blue-100",
      icon: <EyeIcon className="size-4" />,
    },
    editor: {
      label: "Can edit",
      className: "text-purple-800 bg-purple-100",
      icon: "✏️",
    },
  } as const;

  const badge = config[access_type];
  if (!badge) return null;

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${badge.className}`}
    >
      {badge.icon} {badge.label}
    </span>
  );
};

// action and action buttons
DealsTableCard.Actions = function Actions({
  children,
}: {
  children: ReactNode;
}) {
  return <div className="flex items-center gap-2">{children}</div>;
};

DealsTableCard.CreateRowBtn = function CreateRowBtn() {
  const { isTableOpen, toggleTableOpen, handleAddRow } = useDealsTableCard();

  return (
    <HoverExpandButton
      label="Create Order"
      title="Create a new row"
      onClick={() => {
        if (!isTableOpen) toggleTableOpen();
        handleAddRow();
      }}
    >
      <PlusCircleIcon className="w-5 h-5" />
    </HoverExpandButton>
  );
};

DealsTableCard.ExpandBtn = function ExpandBtn() {
  const { isExpanded, isTableOpen, toggleExpand } = useDealsTableCard();

  if (!isTableOpen) return null;

  return (
    <button
      onClick={toggleExpand}
      title={isExpanded ? "Collapse table" : "Expand table"}
      className="p-1 rounded-lg hover:text-custom-blue hover:scale-90 hover:bg-gray-100"
    >
      {isExpanded ? <ArrowsPointingInIcon /> : <ArrowsPointingOutIcon />}
    </button>
  );
};

DealsTableCard.TableAIAutomateBtn = function () {
  const { table } = useDealsTableCard();

  return <TableAIAutomateBtn tableId={table.id} />;
};

DealsTableCard.PinBtn = function () {
  const { table } = useDealsTableCard();

  return <PinBtn id={table.id} is_pinned={table.is_pin} />;
};

DealsTableCard.AccessControlBtn = function () {
  const { table } = useDealsTableCard();

  return <AccessControlBtn tableId={table.id} tableTitle={table.title} />;
};

DealsTableCard.ShowHiddenBtn = function ShowHiddenBtn() {
  const { hiddenColsCount, handleShowAllHidenCol } = useDealsTableCard();

  const isDisabled = hiddenColsCount === 0;

  return (
    <button
      onClick={handleShowAllHidenCol}
      className={`
            relative 
            ${
              hiddenColsCount > 0
                ? "p-1 rounded-lg hover:text-custom-blue hover:scale-90 hover:bg-gray-100"
                : "disabled:cursor-not-allowed disabled:text-gray-400"
            }
        `}
      disabled={isDisabled}
      title="Show hiden columns"
    >
      <EyeIcon className="size-6" />
      {hiddenColsCount > 0 && (
        <small className="absolute -top-[2px] right-0 bg-custom-blue-medium p-[2px] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
          {hiddenColsCount}
        </small>
      )}
    </button>
  );
};

DealsTableCard.DeleteBtn = function DeleteBtn() {
  const { table } = useDealsTableCard();

  return <DeleteTableModal id={table.id} title={table.title} />;
};

DealsTableCard.ExportFileBtn = function ExportBtn() {
  const { rowData, onBtnCsvExport } = useDealsTableCard();

  if (!onBtnCsvExport || rowData.length === 0) return null;

  return <FileExportBtn onBtnCsvExport={onBtnCsvExport} />;
};

// body components
DealsTableCard.Body = function Body({ children }: { children: ReactNode }) {
  const { isTableOpen, isExpanded } = useDealsTableCard();

  return (
    <div
      className="overflow-hidden transition-all duration-500 ease-in-out"
      style={{
        height: !isTableOpen ? 0 : isExpanded ? 700 : 350,
        opacity: !isTableOpen ? 0 : 1, // fades in/out smoothly
      }}
      id="ag-grid-root-container"
    >
      {children}
    </div>
  );
};
